'use client';

import React, { use, useState } from 'react';
import LaporanHeader from '@/components/yayasan/keuangan/LaporanHeader';
import { SchoolUnit, validUnits, getUnitFinancialStats } from '@/lib/data';
import { notFound } from 'next/navigation';
import { PieChart, TrendingUp, TrendingDown, Layers, Calculator } from 'lucide-react';

export default function LabaRugiPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);
    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(initialUnit as SchoolUnit);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const stats = getUnitFinancialStats(activeUnit);

    const INCOME_ITEMS = [
        { name: 'Pendapatan Unit (SPP & BOS)', amount: stats.income },
        { name: 'Pendapatan Hibah & Donasi', amount: 'Rp 0' },
        { name: 'Pendapatan Lainnya', amount: 'Rp 0' },
    ];

    const EXPENSE_ITEMS = [
        { name: 'Beban Operasional Unit', amount: stats.expense },
        { name: 'Beban Gaji & Honorarium', amount: 'Rp 0' },
        { name: 'Beban Pemeliharaan', amount: 'Rp 0' },
    ];

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            <LaporanHeader
                title="Laporan Laba Rugi"
                description="Performa keuangan unit berdasarkan pendapatan dan beban selama periode tertentu"
                activeUnit={activeUnit}
                onUnitChange={setActiveUnit}
            />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Main Financial Statement */}
                <div className="lg:col-span-3 bg-surface p-10 md:p-16 rounded-4xl border border-border-main shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary-soft/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

                    <div className="space-y-12 relative z-10">
                        {/* Header Summary for Audit */}
                        <div className="text-center border-b border-border-main pb-8 space-y-2">
                            <h2 className="text-2xl font-black uppercase tracking-widest text-foreground">Statement of Profit or Loss</h2>
                            <p className="text-xs font-bold text-muted uppercase tracking-[0.3em]">Periode: 1 Februari 2026 - 28 Februari 2026</p>
                        </div>

                        {/* Income Section */}
                        <div className="space-y-6">
                            <div className="flex items-center gap-3 border-b-2 border-primary/20 pb-2">
                                <TrendingUp className="w-5 h-5 text-success" />
                                <h3 className="text-lg font-black uppercase tracking-widest text-primary">PENDAPATAN (REVENUE)</h3>
                            </div>

                            <div className="space-y-4">
                                {INCOME_ITEMS.map((item, i) => (
                                    <div key={i} className="flex justify-between items-center text-sm font-bold group">
                                        <span className="text-secondary group-hover:text-foreground transition-colors">{item.name}</span>
                                        <span className="tabular-nums text-foreground">{item.amount}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-4 border-t border-border-main">
                                    <span className="text-base font-black uppercase tracking-widest text-foreground">TOTAL PENDAPATAN</span>
                                    <span className="text-xl font-black text-primary underline decoration-primary/30 underline-offset-8">{stats.income}</span>
                                </div>
                            </div>
                        </div>

                        {/* Expense Section */}
                        <div className="space-y-6 pt-4">
                            <div className="flex items-center gap-3 border-b-2 border-error/20 pb-2">
                                <TrendingDown className="w-5 h-5 text-error" />
                                <h3 className="text-lg font-black uppercase tracking-widest text-error">BEBAN (EXPENSES)</h3>
                            </div>

                            <div className="space-y-4">
                                {EXPENSE_ITEMS.map((item, i) => (
                                    <div key={i} className="flex justify-between items-center text-sm font-bold group">
                                        <span className="text-secondary group-hover:text-foreground transition-colors">{item.name}</span>
                                        <span className="tabular-nums text-foreground">{item.amount}</span>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-4 border-t border-border-main">
                                    <span className="text-base font-black uppercase tracking-widest text-foreground">TOTAL BEBAN</span>
                                    <span className="text-xl font-black text-error underline decoration-error/30 underline-offset-8">{stats.expense}</span>
                                </div>
                            </div>
                        </div>

                        {/* Final Net Result */}
                        <div className="mt-12 p-8 bg-primary/5 rounded-3xl border-2 border-primary/20 relative overflow-hidden group hover:bg-primary transition-all duration-500">
                            <Layers className="absolute right-0 bottom-0 w-32 h-32 text-primary opacity-10 group-hover:text-white/20 transition-all" />
                            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div>
                                    <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-primary group-hover:text-white/80 transition-all">Net Profit / Loss</h4>
                                    <p className="text-3xl font-black text-foreground group-hover:text-white transition-all uppercase tracking-tight">Laba Bersih Operasional</p>
                                </div>
                                <div className="text-right">
                                    <span className="text-4xl font-black text-primary group-hover:text-white transition-all tabular-nums tracking-tighter decoration-4">{stats.balance}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Insight */}
                <div className="space-y-6">
                    <div className="bg-surface p-7 rounded-3xl border border-border-main shadow-sm space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-success text-white rounded-lg"><Calculator className="w-4 h-4" /></div>
                            <h4 className="font-bold text-sm">Rasio Efisiensi</h4>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between text-xs font-bold text-muted uppercase">
                                <span>Expense/Income</span>
                                <span className="text-primary">66.5%</span>
                            </div>
                            <div className="h-2 w-full bg-background rounded-full overflow-hidden">
                                <div className="h-full w-[66.5%] bg-primary rounded-full transition-all duration-1000" />
                            </div>
                        </div>
                        <p className="text-[10px] text-secondary font-medium leading-relaxed italic">
                            * Persentase beban terhadap total pendapatan unit. Target ideal di bawah 70%.
                        </p>
                    </div>

                    <div className="bg-surface p-7 rounded-3xl border border-border-main shadow-sm space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-warning text-white rounded-lg"><PieChart className="w-4 h-4" /></div>
                            <h4 className="font-bold text-sm">Struktur Pendapatan</h4>
                        </div>
                        <div className="space-y-3">
                            {[
                                { label: 'SPP Siswa', val: '68%' },
                                { label: 'Dana BOS', val: '18%' },
                                { label: 'Lainnya', val: '14%' }
                            ].map((s, i) => (
                                <div key={i} className="flex items-center justify-between text-xs">
                                    <span className="font-bold text-muted uppercase tracking-wider">{s.label}</span>
                                    <span className="font-black text-foreground">{s.val}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
